import { useCallback, useEffect, useRef, useState } from 'react'

import type { ExcludeArea } from '@ai-color-modules/simulation/types'

import type { ImageMetrics, OriginalDimensions } from './useImageMetrics'
import { getImageMetrics } from './utils/imageMetrics'

interface UseExcludeAreaSelectionOptions {
  getMetrics: () => ImageMetrics
  originalDimensions: OriginalDimensions | null
  excludeEnabled: boolean
  setExcludeEnabled: (enabled: boolean) => void
}

/**
 * 除外エリア選択処理のhook
 * 結果画像上でクリック・ドラッグしてポリゴンを描画し、除外エリアを選択する
 */
export const useExcludeAreaSelection = ({
  getMetrics,
  originalDimensions,
  excludeEnabled,
  setExcludeEnabled,
}: UseExcludeAreaSelectionOptions) => {
  const [excludeAreas, setExcludeAreas] = useState<ExcludeArea[]>([])
  const [selectedExcIdx, setSelectedExcIdx] = useState<number>(0)
  const [draggingExcPointIndex, setDraggingExcPointIndex] = useState<number | null>(null)
  const [isExcDragging, setIsExcDragging] = useState(false)
  const [hasDragged, setHasDragged] = useState(false)
  const overlayRectRef = useRef<DOMRect | null>(null)
  const skipNextClickRef = useRef(false)
  const excludeAreasRef = useRef<ExcludeArea[]>([])
  /** 編集操作の undo 履歴 (excludeAreas 全体のスナップショット) */
  const [history, setHistory] = useState<ExcludeArea[][]>([])
  /**
   * 現在のドラッグで履歴を積んだか。state (hasDragged) は同一レンダー内の連続 move で
   * stale になり二重 push しうるため、ref で同期的にガードする
   */
  const dragHistoryPushedRef = useRef(false)
  /** 選択中の頂点インデックス (右クリックメニュー / Delete キー削除の対象) */
  const [selectedVertexIndex, setSelectedVertexIndex] = useState<number | null>(null)

  /** undo 履歴の最大保持数 (useSubAreaSelection と同じ値) */
  const MAX_HISTORY = 30

  const pushHistory = useCallback((snapshot: ExcludeArea[]) => {
    setHistory(prev => [...prev.slice(-(MAX_HISTORY - 1)), snapshot])
  }, [])

  // 編集対象エリアが変わったら頂点選択を解除
  useEffect(() => {
    setSelectedVertexIndex(null)
  }, [selectedExcIdx])

  const setExcludeAreasWithRef = useCallback<React.Dispatch<React.SetStateAction<ExcludeArea[]>>>(
    value => {
      const nextAreas =
        typeof value === 'function'
          ? (value as (prev: ExcludeArea[]) => ExcludeArea[])(excludeAreasRef.current)
          : value

      excludeAreasRef.current = nextAreas
      setExcludeAreas(nextAreas)
    },
    [],
  )

  useEffect(() => {
    excludeAreasRef.current = excludeAreas
  }, [excludeAreas])

  useEffect(() => {
    if (excludeAreas.length === 0) {
      setSelectedExcIdx(0)
      return
    }
    if (selectedExcIdx >= excludeAreas.length) {
      setSelectedExcIdx(excludeAreas.length - 1)
    }
  }, [excludeAreas.length, selectedExcIdx])

  useEffect(() => {
    if (excludeEnabled) {
      return
    }

    setDraggingExcPointIndex(null)
    setIsExcDragging(false)
    setHasDragged(false)
    setSelectedVertexIndex(null)
    skipNextClickRef.current = false
    overlayRectRef.current = null
  }, [excludeEnabled])

  /**
   * 除外エリアのポイントドラッグ開始
   */
  const handleExcDragStart = (e: React.MouseEvent | React.TouchEvent, pointIndex: number) => {
    const currentArea = excludeAreas[selectedExcIdx]
    if (!currentArea?.points[pointIndex]) {
      return
    }

    e.preventDefault()
    e.stopPropagation()
    setHasDragged(false)
    setDraggingExcPointIndex(pointIndex)
    setIsExcDragging(true)
    // 頂点の押下 = 頂点選択 (選択モードの「頂点選択」)
    setSelectedVertexIndex(pointIndex)
    dragHistoryPushedRef.current = false
    skipNextClickRef.current = true
  }

  const handleExcDragEnd = () => {
    if (isExcDragging) {
      setIsExcDragging(false)
      setDraggingExcPointIndex(null)
      setHasDragged(true)
      overlayRectRef.current = null
      skipNextClickRef.current = true
      window.setTimeout(() => {
        skipNextClickRef.current = false
      }, 0)
      return
    }

    skipNextClickRef.current = false
  }

  /**
   * 結果画像オーバーレイの移動処理
   * パフォーマンス最適化: ドラッグ開始時にrectをキャッシュし、ドラッグ中は再利用
   */
  const handleResultOverlayMove = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
    rect: DOMRect,
  ) => {
    const currentArea = excludeAreas[selectedExcIdx]
    if (!currentArea) {
      return
    }

    // mouseup を取りこぼして isExcDragging が残った場合の自己修復
    if (isExcDragging && 'buttons' in e && (e.buttons & 1) === 0) {
      handleExcDragEnd()
      return
    }
    if ('touches' in e && isExcDragging) {
      e.preventDefault()
      e.stopPropagation()
    }

    if (isExcDragging && draggingExcPointIndex !== null && originalDimensions) {
      if (!overlayRectRef.current) {
        overlayRectRef.current = rect
      }
      const cachedRect = overlayRectRef.current

      const touch = 'touches' in e ? e.touches[0] : undefined
      if ('touches' in e && !touch) return
      const clientX = touch?.clientX ?? ('clientX' in e ? e.clientX : 0)
      const clientY = touch?.clientY ?? ('clientY' in e ? e.clientY : 0)

      const metrics = getImageMetrics(getMetrics, originalDimensions)
      if (!metrics) return
      const { dispW, dispH } = metrics

      const scaleX = cachedRect.width / dispW
      const scaleY = cachedRect.height / dispH
      const imgX = (clientX - cachedRect.left) / scaleX
      const imgY = (clientY - cachedRect.top) / scaleY

      if (imgX < 0 || imgY < 0 || imgX > dispW || imgY > dispH) return

      if (!hasDragged) setHasDragged(true)
      if (!dragHistoryPushedRef.current) {
        dragHistoryPushedRef.current = true
        // 1 ドラッグ操作 = undo 1 ステップ。実際に動き始めた時点の状態を積む
        // (excludeAreasRef は move 適用前なので pre-drag スナップショットになる)
        pushHistory(excludeAreasRef.current)
      }

      setExcludeAreasWithRef(prev => {
        const nextAreas = [...prev]
        const target = nextAreas[selectedExcIdx]
        if (!target || !target.points[draggingExcPointIndex]) {
          return prev
        }

        const nextPoints = [...target.points]
        nextPoints[draggingExcPointIndex] = {
          originalX: (imgX / dispW) * originalDimensions.width,
          originalY: (imgY / dispH) * originalDimensions.height,
        }

        nextAreas[selectedExcIdx] = {
          ...target,
          points: nextPoints,
        }
        return nextAreas
      })
    }
  }

  /**
   * 結果画像クリックで頂点追加
   */
  const handleResultOverlayClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const currentArea = excludeAreas[selectedExcIdx]
    if (!excludeEnabled || !originalDimensions || !currentArea) return
    if (hasDragged || isExcDragging || draggingExcPointIndex !== null || skipNextClickRef.current) {
      setHasDragged(false)
      skipNextClickRef.current = false
      return
    }

    const rect = e.currentTarget.getBoundingClientRect()
    const metrics = getImageMetrics(getMetrics, originalDimensions)
    if (!metrics) return
    const { dispW, dispH } = metrics

    const scaleX = rect.width / dispW
    const scaleY = rect.height / dispH
    const imgX = (e.clientX - rect.left) / scaleX
    const imgY = (e.clientY - rect.top) / scaleY

    if (imgX < 0 || imgY < 0 || imgX > dispW || imgY > dispH) return

    const originalX = (imgX / dispW) * originalDimensions.width
    const originalY = (imgY / dispH) * originalDimensions.height

    pushHistory(excludeAreasRef.current)
    setExcludeAreasWithRef(prev => {
      const nextAreas = [...prev]
      const target = nextAreas[selectedExcIdx]
      if (!target) {
        return prev
      }

      nextAreas[selectedExcIdx] = {
        ...target,
        points: [...target.points, { originalX, originalY }],
      }
      return nextAreas
    })
  }

  /**
   * 除外エリアを追加する
   */
  const handleAddExcludeArea = () => {
    const nextIndex = excludeAreasRef.current.length
    setExcludeAreasWithRef(prev => [...prev, { id: Date.now(), points: [] }])
    setSelectedExcIdx(nextIndex)
    setHasDragged(false)
    skipNextClickRef.current = false
  }

  /**
   * 除外エリアをリセットする
   */
  const resetExcludeArea = (idx: number) => {
    setExcludeAreasWithRef(prev => {
      const target = prev[idx]
      if (!target) {
        return prev
      }

      const nextAreas = [...prev]
      nextAreas[idx] = { ...target, points: [] }
      return nextAreas
    })
  }

  /**
   * 除外エリアを削除する
   */
  const removeExcludeArea = (idx: number) => {
    const nextAreas = excludeAreasRef.current.filter((_, i) => i !== idx)
    setExcludeAreasWithRef(nextAreas)
    setSelectedExcIdx(prev => {
      if (nextAreas.length === 0) {
        return 0
      }
      if (prev > idx) {
        return prev - 1
      }
      if (prev === idx) {
        return Math.min(idx, nextAreas.length - 1)
      }
      return Math.min(prev, nextAreas.length - 1)
    })
    setDraggingExcPointIndex(null)
    setIsExcDragging(false)
    setHasDragged(false)
    skipNextClickRef.current = false
    overlayRectRef.current = null
  }

  /**
   * 辺上に頂点を挿入する
   * @param edgeIndex 挿入する辺のインデックス（この辺の後に挿入される）
   * @param originalX 挿入する座標X（オリジナル座標系）
   * @param originalY 挿入する座標Y（オリジナル座標系）
   */
  const handleInsertExcPoint = (edgeIndex: number, originalX: number, originalY: number) => {
    const currentArea = excludeAreasRef.current[selectedExcIdx]
    if (!currentArea) {
      return
    }

    pushHistory(excludeAreasRef.current)
    setExcludeAreasWithRef(prev => {
      const nextAreas = [...prev]
      const target = nextAreas[selectedExcIdx]
      if (!target) {
        return prev
      }

      const nextPoints = [...target.points]
      // edgeIndex + 1 の位置に挿入
      nextPoints.splice(edgeIndex + 1, 0, { originalX, originalY })

      nextAreas[selectedExcIdx] = {
        ...target,
        points: nextPoints,
      }
      return nextAreas
    })
  }

  /**
   * 頂点を削除する
   * @param pointIndex 削除する頂点のインデックス
   */
  const handleDeleteExcPoint = (pointIndex: number) => {
    const currentArea = excludeAreasRef.current[selectedExcIdx]
    if (!currentArea) {
      return
    }

    pushHistory(excludeAreasRef.current)
    // 削除で頂点インデックスがズレるため選択は解除する
    setSelectedVertexIndex(null)
    setExcludeAreasWithRef(prev => {
      const nextAreas = [...prev]
      const target = nextAreas[selectedExcIdx]
      if (!target) {
        return prev
      }

      const nextPoints = [...target.points]
      nextPoints.splice(pointIndex, 1)

      nextAreas[selectedExcIdx] = {
        ...target,
        points: nextPoints,
      }
      return nextAreas
    })
  }

  /**
   * 直前の編集操作 (頂点の追加・移動・削除) を 1 ステップ取り消す
   */
  const undo = () => {
    if (history.length === 0) return
    const last = history[history.length - 1]
    setHistory(prev => prev.slice(0, -1))
    setSelectedVertexIndex(null)
    setExcludeAreasWithRef(last)
  }

  /** undo 履歴を破棄する (タブ切替・保存データ復元時に呼ぶ) */
  const resetHistory = useCallback(() => {
    setHistory([])
    setSelectedVertexIndex(null)
  }, [])

  /** 頂点を選択する (右クリックメニュー表示時など) */
  const selectVertex = (pointIndex: number) => {
    setSelectedVertexIndex(pointIndex)
  }

  /** 選択中の頂点を削除する (Delete キー / 右クリックメニュー) */
  const deleteSelectedVertex = () => {
    if (selectedVertexIndex === null) return
    handleDeleteExcPoint(selectedVertexIndex)
  }

  return {
    excludeEnabled,
    setExcludeEnabled,
    excludeAreas,
    setExcludeAreas: setExcludeAreasWithRef,
    selectedExcIdx,
    setSelectedExcIdx,
    draggingExcPointIndex,
    isExcDragging,
    handleExcDragStart,
    handleExcDragEnd,
    handleResultOverlayMove,
    handleResultOverlayClick,
    handleAddExcludeArea,
    resetExcludeArea,
    removeExcludeArea,
    handleInsertExcPoint,
    handleDeleteExcPoint,
    selectedVertexIndex,
    selectVertex,
    deleteSelectedVertex,
    undo,
    canUndo: history.length > 0,
    resetHistory,
  }
}
